import getConfig from 'next/config'
import { MOBILE_APP_EVENTS } from './constant'
import { GetServerSidePropsContext } from 'next/types'
import {
  ICheckoutCCardPayPayload,
  ICheckoutConsentTappedPayload,
  ICheckoutReviewVideoImpressionEndPayload,
  ICheckoutReviewVideoImpressionStartPayload,
  ICheckoutReviewViewPayload,
  ICheckoutViewAppearPayload,
  ICheckoutViewDisappearPayload,
  IMessagePayload,
  IShippingChangePayload,
  IChangePaymentMethodTappedPayload,
  IChangeShippingAddressTappedPayload,
  ICheckoutApplePayCancelPayload,
  ICheckoutPaypalLoggedInPayload,
  IPaymentFailedPayload,
  IPromoRevealAppearPayload,
  IPromoRevealDisappearPayload,
  IPromoClaimTappedPayload,
  ITrackingEventPayload
} from 'src/types/interfaces/event'
import { logDebug } from './debug'

declare global {
  interface Window {
    webkit?: {
      messageHandlers: {
        iOSHandler: {
          postMessage: (message: any) => void
        }
      }
    }
    handleiOSMessage?: (message: any) => void
    /**
     * Native bridge. Its bootstrap script is injected at document start, main
     * frame only, so it already exists before any of our own scripts run -
     * there's no readiness check or race to handle. `getServerTime()` never
     * rejects: outside the app, or on native failure, it resolves to the device
     * clock with `trusted: false, source: 'device'`, so the same web build runs
     * unchanged in a desktop browser.
     *
     * Not to be confused with `webkit.messageHandlers.iOSHandler` above, which
     * is the one-way channel sendMessageToApp posts events to.
     */
    ShopHelpNative?: {
      getServerTime: () => Promise<IServerTimeBridgeResult>
    }
  }
}

export interface IServerTimeBridgeResult {
  /** Epoch milliseconds, integer. */
  serverTimeMs: number
  /** True only when the value came from a real server anchor. */
  trusted: boolean
  /** 'native' inside the app, 'device' in a browser or if the bridge fails. */
  source: 'native' | 'device'
}

export const toDictionary = <T, K>(
  arr: T[],
  keyFn: (item: T) => string,
  itemFn: (item: T) => K
) => {
  return arr.reduce((total, item) => {
    total[keyFn(item)] = itemFn(item)
    return total
  }, {} as { [key: string]: K })
}

export const mappingStateCode = {
  alabama: 'al',
  alaska: 'ak',
  americansamoa: 'as',
  arizona: 'az',
  arkansas: 'ar',
  california: 'ca',
  colorado: 'co',
  connecticut: 'ct',
  delaware: 'de',
  districtofcolumbia: 'dc',
  federatedstatesofmicronesia: 'fm',
  florida: 'fl',
  georgia: 'ga',
  guam: 'gu',
  hawaii: 'hi',
  idaho: 'id',
  illinois: 'il',
  indiana: 'in',
  iowa: 'ia',
  kansas: 'ks',
  kentucky: 'ky',
  louisiana: 'la',
  maine: 'me',
  marshallislands: 'mh',
  maryland: 'md',
  massachusetts: 'ma',
  michigan: 'mi',
  minnesota: 'mn',
  mississippi: 'ms',
  missouri: 'mo',
  montana: 'mt',
  nebraska: 'ne',
  nevada: 'nv',
  newhampshire: 'nh',
  newjersey: 'nj',
  newmexico: 'nm',
  newyork: 'ny',
  northcarolina: 'nc',
  northdakota: 'nd',
  northernmarianaislands: 'mp',
  ohio: 'oh',
  oklahoma: 'ok',
  oregon: 'or',
  palau: 'pw',
  pennsylvania: 'pa',
  puertorico: 'pr',
  rhodeisland: 'ri',
  southcarolina: 'sc',
  southdakota: 'sd',
  tennessee: 'tn',
  texas: 'tx',
  utah: 'ut',
  vermont: 'vt',
  virginislands: 'vi',
  virginia: 'va',
  washington: 'wa',
  westvirginia: 'wv',
  wisconsin: 'wi',
  wyoming: 'wy'
}

type AppMessageType = keyof typeof MOBILE_APP_EVENTS

type AppMessagePayload = {
  [MOBILE_APP_EVENTS.CHECKOUT_REVIEW_VIEW]: ICheckoutReviewViewPayload
  [MOBILE_APP_EVENTS.CHECKOUT_REVIEW_VIDEO_IMPRESSION_START]: ICheckoutReviewVideoImpressionStartPayload
  [MOBILE_APP_EVENTS.CHECKOUT_REVIEW_VIDEO_IMPRESSION_END]: ICheckoutReviewVideoImpressionEndPayload
  [MOBILE_APP_EVENTS.SHIPPING_CHANGE]: IShippingChangePayload
  [MOBILE_APP_EVENTS.CHECKOUT_CONSENT_TAPPED]: ICheckoutConsentTappedPayload
  [MOBILE_APP_EVENTS.CHECKOUT_CARD_PAY]: ICheckoutCCardPayPayload
  [MOBILE_APP_EVENTS.CHECKOUT_VIEW_APPEAR]: ICheckoutViewAppearPayload
  [MOBILE_APP_EVENTS.CHECKOUT_VIEW_DISAPPEAR]: ICheckoutViewDisappearPayload
  [MOBILE_APP_EVENTS.CHANGE_PAYMENT_METHOD_TAPPED]: IChangePaymentMethodTappedPayload
  [MOBILE_APP_EVENTS.CHANGE_SHIPPING_ADDRESS_TAPPED]: IChangeShippingAddressTappedPayload
  [MOBILE_APP_EVENTS.CHECKOUT_APPLE_PAY_CANCEL]: ICheckoutApplePayCancelPayload
  [MOBILE_APP_EVENTS.CHECKOUT_PAYPAL_LOGGEDIN]: ICheckoutPaypalLoggedInPayload
  [MOBILE_APP_EVENTS.PAYMENT_FAILED]: IPaymentFailedPayload
  [MOBILE_APP_EVENTS.TRACKING_EVENT]: ITrackingEventPayload
  [MOBILE_APP_EVENTS.PROMO_REVEAL_APPEAR]: IPromoRevealAppearPayload
  [MOBILE_APP_EVENTS.PROMO_REVEAL_DISAPPEAR]: IPromoRevealDisappearPayload
  [MOBILE_APP_EVENTS.PROMO_CLAIM_TAPPED]: IPromoClaimTappedPayload
  [key: string]: IMessagePayload
}

interface IAppMessage<T extends AppMessageType> {
  type: T
  payload?: AppMessagePayload[T]
  [x: string]: any
}

/**
 * Mapping of app client versions to their corresponding build numbers. This is used to determine which features or messages to send based on the app version.
 * Please refer to https://crossian.atlassian.net/wiki/spaces/tech/pages/1944682514/ShopHelp+iOS+App+-+Release+Version+List#4.-Version-Control for the latest version information.
 */
type AppClientVersion = {
  [key: string]: number
}
const appDevClientVersion: AppClientVersion = {
  ['1.0.0']: 37,
  ['1.0.1']: 43,
  ['1.1.0']: 49,
  ['1.2.0']: 53,
  ['1.3.0']: 57,
  ['1.4.0']: 61,
  ['1.5.0']: 73,
  ['1.6.0']: 78,
  ['1.7.0']: 83,
  ['1.8.0']: 85,
  ['1.9.0']: 91,
  ['1.10.0']: 106,
  ['1.11.0']: 110,
  ['1.12.0']: 112,
  ['1.13.0']: 115,
  ['1.14.0']: 126,
  ['2.2.0']: 147
}

const appStgClientVersion: AppClientVersion = {
  ['1.0.0']: 37,
  ['1.0.1']: 43,
  ['1.1.0']: 49,
  ['1.2.0']: 53,
  ['1.3.0']: 57,
  ['1.4.0']: 61,
  ['1.5.0']: 73,
  ['1.6.0']: 78,
  ['1.7.0']: 83,
  ['1.8.0']: 85,
  ['1.9.0']: 91,
  ['1.10.0']: 106,
  ['1.11.0']: 110,
  ['1.12.0']: 112,
  ['1.13.0']: 115,
  //legit from 1.14.0, the old versions are just fullback
  ['1.14.0']: 119,
  ['2.2.0']: 140
}

const appProdClientVersion: AppClientVersion = {
  ['1.0.0']: 37,
  ['1.0.1']: 43,
  ['1.1.0']: 49,
  ['1.2.0']: 53,
  ['1.3.0']: 57,
  ['1.4.0']: 61,
  ['1.5.0']: 73,
  ['1.6.0']: 78,
  ['1.7.0']: 83,
  ['1.8.0']: 86,
  ['1.9.0']: 91,
  ['1.10.0']: 107,
  ['1.11.0']: 110,
  ['1.12.0']: 112,
  ['1.13.0']: 118,
  ['1.14.0']: 126,
  ['2.2.0']: 140
}

export const appClientVersion =
  {
    local: appDevClientVersion,
    dev: appDevClientVersion,
    stg: appStgClientVersion,
    prod: appProdClientVersion
  }[getConfig().publicRuntimeConfig.env as string] ?? appDevClientVersion

/**
 * Naming for app version
 */
export const APP_VERSION = {
  EDIT_PAYMENT: appClientVersion['1.12.0']
}

export function sendMessageToApp<T extends AppMessageType>(message: IAppMessage<T>) {
  logDebug(`SendMessageToApp | ${message.type.toUpperCase()} | `, message)
  if (
    window.webkit &&
    window.webkit?.messageHandlers &&
    window.webkit?.messageHandlers?.iOSHandler
  ) {
    window.webkit.messageHandlers.iOSHandler.postMessage(JSON.stringify(message))
  } else {
    console.log('Not in iOS WebView or handler not available')
  }
}

export enum EventCategory {
  USER_ENGAGEMENT = 'user_engagement',
  SYSTEM_EVENT = 'system_event'
}
export function sendTrackingEvent<TProperties extends Record<string, unknown>>(
  name: string,
  categoryOrProperties?: EventCategory | TProperties,
  properties?: TProperties
) {
  let category: EventCategory = EventCategory.USER_ENGAGEMENT
  if (isEnumInstance(EventCategory, categoryOrProperties)) {
    category = categoryOrProperties as EventCategory
  } else if (typeof categoryOrProperties === 'object') {
    properties = categoryOrProperties
  }

  const payload: ITrackingEventPayload = { name: name.toLowerCase(), category }
  if (properties) {
    payload.properties = properties
  }

  sendMessageToApp({
    type: MOBILE_APP_EVENTS.TRACKING_EVENT,
    payload
  })
}

export function decodeJSONParameter<T>(value: string, fallback?: T) {
  if (!value) return fallback
  try {
    return JSON.parse(decodeURIComponent(value))
  } catch {
    try {
      return JSON.parse(value)
    } catch {
      return fallback
    }
  }
}

export function decodeStringParameter(value?: string, fallback = '') {
  if (!value) return fallback
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function isValidIsoDateString(value?: string): boolean {
  return !!value && !Number.isNaN(new Date(value).getTime())
}

export function isEnumInstance(enumObject: any, value: any): boolean {
  return Object.values(enumObject).includes(value)
}

/**
 * A single reading of the server clock, paired with the monotonic
 * `performance.now()` timestamp it was taken at. Anchoring once and advancing
 * by the monotonic delta keeps the clock correct even if the user changes the
 * device date mid-session, which a plain `Date.now()` offset can't do.
 */
export interface IServerClockAnchor {
  /** Server epoch ms at the moment the anchor was captured. */
  serverTimeMs: number
  /** Monotonic clock reading (see monotonicNow) at that same moment. */
  at: number
}

/**
 * `performance.now()` where it exists, `Date.now()` otherwise. `performance`
 * isn't a global on older Node versions, and this module is also pulled in
 * server-side during SSR, so reaching for it unguarded would throw there.
 * Both the anchor and the reads off it go through this, so a fallback just
 * degrades to plain wall-clock deltas rather than mixing two time bases.
 */
export function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

/** True when the page is running inside the app, with the native bridge present. */
export function isShopHelpNativeAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.ShopHelpNative?.getServerTime === 'function'
}

/**
 * Anchors the server clock off the native bridge, whose bootstrap script the
 * app injects at document start (no readiness check to handle). Resolves to
 * undefined whenever there's no usable server anchor - outside the app, on an
 * older build without the bridge, or when the bridge itself reports
 * `trusted: false` (it had no server anchor yet, e.g. rebooted with no API call
 * since, and fell back to the user-settable device clock). Callers should treat
 * undefined as "no server clock" rather than substituting a device timestamp.
 */
export async function requestServerClockAnchorFromApp(): Promise<IServerClockAnchor | undefined> {
  if (!isShopHelpNativeAvailable()) return undefined
  try {
    const { serverTimeMs, trusted } = await window.ShopHelpNative!.getServerTime()
    if (!trusted || !Number.isFinite(serverTimeMs)) return undefined
    return { serverTimeMs, at: monotonicNow() }
  } catch {
    // The wrapper is documented never to reject, but a bridge failure must
    // never take checkout down with it.
    return undefined
  }
}

/**
 * Current server time in epoch ms, advanced from the anchor by the monotonic
 * `performance.now()` delta. Falls back to the device clock when there's no
 * anchor at all.
 */
export function serverNow(anchor?: IServerClockAnchor): number {
  if (!anchor) return Date.now()
  return anchor.serverTimeMs + (monotonicNow() - anchor.at)
}

export const COOKIE_MAX_AGE = 31536000

export function appendXHeadersToCookies(context: GetServerSidePropsContext) {
  const { req, res } = context

  const existingCookies = res.getHeader('Set-Cookie')
  const cookies: string[] = existingCookies
    ? Array.isArray(existingCookies)
      ? existingCookies.map(String)
      : [String(existingCookies)]
    : []

  Object.entries(req.headers).forEach(([key, value]) => {
    if ((key.startsWith('x-') || key.startsWith('X-')) && value) {
      cookies.push(`${key}=${value}; Path=/; Secure; SameSite=Lax; Max-Age=${COOKIE_MAX_AGE}`)
    }
  })

  res.setHeader('Set-Cookie', cookies)
}
